import React from 'react';

export default function Careers() {
  const openings = [
    { title: 'Multimodal AI Research Scientist', department: 'Research & Development', location: 'Remote', type: 'Full-time', description: 'Lead research in multimodal fusion architectures combining acoustic, radiology, and pathology data for clinical diagnostics.' },
    { title: 'MedTech Full-Stack Engineer', department: 'Engineering', location: 'Remote', type: 'Full-time', description: 'Build scalable web applications for CTF clinical intelligence platform using React, Python, and PyTorch.' },
    { title: 'Clinical Product Manager (MedTech)', department: 'Product', location: 'Remote', type: 'Full-time', description: 'Define product roadmap for AI-powered clinical diagnostics and regulatory strategy (FDA/HIPAA/GDPR).' },
    { title: 'Machine Learning Engineer (Healthcare AI)', department: 'AI/ML', location: 'Remote', type: 'Full-time', description: 'Develop and deploy ML models for medical diagnostics using PyTorch, Hugging Face, and multi-omics pipelines.' },
    { title: 'Embedded Systems Engineer (Firmware)', department: 'Hardware Engineering', location: 'Remote', type: 'Full-time', description: 'Build STM32 bootloaders, bare-metal programming, and RTOS for medical device hardware integration.' },
    { title: 'Clinical Research Associate', department: 'Clinical Validation', location: 'Remote', type: 'Contract', description: 'Coordinate clinical validation studies across medical centers and prepare regulatory submission documentation.' },
    { title: 'Biomedical Data Scientist', department: 'Data Science', location: 'Remote', type: 'Full-time', description: 'Fusion architectures for UK Biobank and biomedical databases, multi-omics data analysis.' },
    { title: 'Radiologist (Clinical Advisor)', department: 'Clinical Validation', location: 'Remote', type: 'Contract', description: 'Provide clinical expertise on chest X-ray interpretation and validate radiology AI model outputs for TB and pulmonary disease detection.' },
    { title: 'Pathologist (Clinical Advisor)', department: 'Clinical Validation', location: 'Remote', type: 'Contract', description: 'Guide pathology data annotation and validate AI-driven tissue analysis for multimodal disease classification.' },
    { title: 'Pulmonologist (Clinical Advisor)', department: 'Clinical Validation', location: 'Remote', type: 'Contract', description: 'Advise on clinical relevance of acoustic and radiology biomarkers for pulmonary disease diagnosis and validation study design.' },
  ];

  const benefits = [
    { icon: '🏠', title: 'Remote-First', description: 'Work from anywhere globally.' },
    { icon: '⚕️', title: 'Health Insurance', description: 'Comprehensive medical coverage for you and family.' },
    { icon: '💰', title: 'Competitive Salary + Equity', description: 'Market-leading compensation with startup equity options.' },
    { icon: '📚', title: 'AI Learning Budget', description: '$5,000 annual stipend for AI tools, courses, and research.' },
    { icon: '🌴', title: 'Flexible Time Off', description: 'Unlimited PTO for research-oriented work.' },
    { icon: '🚀', title: 'Stealth Startup Impact', description: 'Build MVPs that will transform global healthcare diagnostics.' },
    { icon: '🔬', title: 'Research Collaboration', description: 'Work with clinicians and academic medical centers.' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-tealLight/30 to-paper text-ink antialiased selection:bg-teal/20 selection:text-ink">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 font-sans">
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-tealLight border border-teal/20 text-teal text-xs font-semibold uppercase tracking-wider">
              Careers
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-sans font-bold leading-tight tracking-tight text-center text-teal">
            Join Our Team
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-inkSoft max-w-3xl mx-auto leading-relaxed text-center">
            Help us revolutionize clinical diagnostics with multimodal AI. We're looking for passionate
            scientists, engineers, and clinicians who want to make a difference in healthcare.
          </p>
        </div>

        <div className="mb-16">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-lg bg-tealLight border border-teal/20 flex items-center justify-center text-lg">💼</span>
              <h2 className="text-2xl font-bold text-ink tracking-tight">Why Join Us?</h2>
            </div>
            <span className="inline-flex items-center gap-1.5 bg-tealLight text-teal text-[10px] font-bold tracking-wider uppercase px-3 py-1.5 rounded-md border border-teal/20">
              <span className="w-1.5 h-1.5 rounded-full bg-teal"></span>
              Benefits
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="group relative bg-white border border-rule rounded-xl p-6 shadow-[4px_4px_0_0_rgba(20,184,166,0.18)] hover:shadow-[6px_6px_0_0_rgba(20,184,166,0.3)] hover:-translate-y-0.5 hover:-translate-x-0.5 transition-all duration-200 text-center"
              >
                <div className="text-3xl mb-3">{benefit.icon}</div>
                <h3 className="text-base font-bold text-ink group-hover:text-teal transition-colors mb-1">{benefit.title}</h3>
                <p className="text-sm text-inkSoft">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-lg bg-tealLight border border-teal/20 flex items-center justify-center text-lg">📋</span>
              <h2 className="text-2xl font-bold text-ink tracking-tight">Open Positions</h2>
            </div>
            <span className="inline-flex items-center gap-1.5 bg-tealLight text-teal text-[10px] font-bold tracking-wider uppercase px-3 py-1.5 rounded-md border border-teal/20">
              <span className="w-1.5 h-1.5 rounded-full bg-teal"></span>
              Join Us
            </span>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {openings.map((job, index) => (
              <div
                key={index}
                className="group relative bg-white border border-rule rounded-xl p-6 shadow-[4px_4px_0_0_rgba(20,184,166,0.18)] hover:shadow-[6px_6px_0_0_rgba(20,184,166,0.3)] hover:-translate-y-0.5 hover:-translate-x-0.5 transition-all duration-200"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-ink group-hover:text-teal transition-colors">{job.title}</h3>
                    <p className="text-sm text-inkSoft">{job.department}</p>
                    <div className="flex flex-wrap gap-3 mt-2">
                      <span className="text-xs bg-tealLight text-teal px-2 py-1 rounded border border-teal/20">{job.location}</span>
                      <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">{job.type}</span>
                    </div>
                    <p className="text-sm text-inkSoft mt-3">{job.description}</p>
                  </div>
                  <button className="bg-teal text-white px-6 py-2 rounded-lg font-semibold hover:bg-teal/90 transition shadow-md whitespace-nowrap">
                    Apply Now →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-ink text-white rounded-xl p-8 text-center shadow-lg">
          <h3 className="text-xl font-bold mb-2 text-white">Don't see the right role?</h3>
          <p className="text-tealMid mb-4">We're building a team of world-class AI researchers, clinicians, and engineers.</p>
          <button className="bg-teal text-white px-8 py-3 rounded-lg font-semibold hover:bg-teal/90 transition shadow-md">
            Send Us Your Resume →
          </button>
        </div>
      </div>
    </div>
  );
}
